/**
 * Парсинг суммы из числа или строки (как приходит из API / форм).
 * @param {unknown} value
 * @returns {number|null}
 */
export function parseAmount (value) {
  if (value == null || value === '') return null
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  const cleaned = String(value).trim().replace(/\s/g, '').replace(/[^\d.,-]/g, '')
  if (!cleaned) return null
  const n = parseFloat(cleaned.replace(',', '.'))
  if (Number.isNaN(n) || !Number.isFinite(n)) return null
  return n
}

/**
 * Опции форматирования рублей.
 * @typedef {object} FormatRubOptions
 * @property {number} [minFrac] минимум дробных знаков (по умолчанию 0)
 * @property {number} [maxFrac] максимум дробных (по умолчанию = minFrac)
 * @property {string} [empty] при нераспознанном значении (по умолчанию '-')
 * @property {boolean} [showZero] показывать «0 ₽» при нуле (по умолчанию false)
 */

/**
 * Форматирование в рубли (ru-RU, символ ₽).
 * @param {unknown} raw
 * @param {FormatRubOptions} [opts]
 */
export function formatRub (raw, opts = {}) {
  const {
    minFrac = 0,
    maxFrac,
    empty = '-',
    showZero = false,
  } = opts
  const maxF = maxFrac != null ? maxFrac : minFrac
  const n = parseAmount(raw)
  if (n === null) return empty
  if (n === 0 && !showZero) return empty
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: minFrac,
    maximumFractionDigits: maxF,
  }).format(n)
}

/** Таблицы доходов: до 2 знаков после запятой, ноль как «0 ₽». */
export function formatRubTable (raw) {
  return formatRub(raw, { minFrac: 0, maxFrac: 2, showZero: true })
}

const REPORT_EMPTY = '—'

/**
 * Печатные отчёты (сводка актов, бюджет договора/стадии, доходы): ровно 2 знака,
 * ноль как «0,00 ₽», отсутствие значения — «—».
 */
export function formatReportRub (raw) {
  return formatRub(raw, {
    minFrac: 2,
    maxFrac: 2,
    showZero: true,
    empty: REPORT_EMPTY,
  })
}

/** Проценты в отчётах: ru-RU, два знака после запятой (как суммы). */
export function formatReportPercent (raw) {
  let n
  if (typeof raw === 'number') {
    n = Number.isFinite(raw) ? raw : null
  } else {
    n = parseAmount(raw)
  }
  if (n === null) return REPORT_EMPTY
  return `${new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}%`
}
